import React, { useState, useEffect } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Alert,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/contexts/AuthContext';
import {
    usePreviewLock,
    PREVIEW_LOCK_NOTE_ID,
} from '@/shared/hooks/usePreviewLock';
import PreviewLockNote from '@/components/common/PreviewLockNote';
import { userService } from '@/shared/services/userService';

// The only field the profile routes name in a refusal's `field`; its form
// control has the same id.
const FORM_FIELD = 'studentId';

function EditProfile() {
    const { userProfile, refreshProfile } = useAuth();
    const navigate = useNavigate();
    const previewLocked = usePreviewLock();
    const isStudent = Boolean(userProfile?.roles?.includes('student'));

    const [formData, setFormData] = useState({
        name: '',
        studentId: '',
        department: '',
        major: '',
        phone: '',
    });
    const [emailNotifications, setEmailNotifications] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [fieldError, setFieldError] = useState(null);

    useEffect(() => {
        if (fieldError) document.getElementById(fieldError.field)?.focus();
    }, [fieldError]);

    useEffect(() => {
        if (userProfile) {
            setFormData({
                name: userProfile.name || '',
                studentId: userProfile.studentId || '',
                department: userProfile.department || '',
                major: userProfile.major || '',
                phone: userProfile.phone || '',
            });
            setEmailNotifications(
                userProfile.preferences?.emailNotifications !== false
            );
        }
    }, [userProfile]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setFieldError((prev) => (prev?.field === name ? null : prev));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');
        setFieldError(null);

        try {
            await userService.updateProfile({
                ...formData,
                preferences: { emailNotifications },
            });
            refreshProfile();
            setSuccess('Profile updated successfully');
        } catch (err) {
            const { field, message } = err.response?.data || {};
            if (field === FORM_FIELD && message) {
                setFieldError({ field, message });
            } else {
                setError('Failed to update profile. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container className="py-4">
            <Row>
                <Col lg={6} className="mx-auto">
                    <Card>
                        <Card.Header>
                            <h4 className="mb-0">Edit Profile</h4>
                        </Card.Header>
                        <Card.Body>
                            {error && <Alert variant="danger">{error}</Alert>}
                            {success && (
                                <Alert variant="success">{success}</Alert>
                            )}

                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-3" controlId="name">
                                    <Form.Label>Full Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                    />
                                </Form.Group>

                                <Form.Group
                                    className="mb-3"
                                    controlId="studentId"
                                >
                                    <Form.Label>
                                        Student ID (900 Number)
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="studentId"
                                        value={formData.studentId}
                                        onChange={handleChange}
                                        placeholder="900XXXXXX"
                                        isInvalid={
                                            fieldError?.field === 'studentId'
                                        }
                                        aria-invalid={
                                            fieldError?.field === 'studentId'
                                        }
                                        aria-describedby={
                                            fieldError?.field === 'studentId'
                                                ? 'studentId-error'
                                                : undefined
                                        }
                                    />
                                    <Form.Control.Feedback
                                        type="invalid"
                                        id="studentId-error"
                                    >
                                        {fieldError?.message}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                {isStudent ? (
                                    <Form.Group
                                        className="mb-3"
                                        controlId="major"
                                    >
                                        <Form.Label>Major</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="major"
                                            value={formData.major}
                                            onChange={handleChange}
                                        />
                                    </Form.Group>
                                ) : (
                                    <Form.Group
                                        className="mb-3"
                                        controlId="department"
                                    >
                                        <Form.Label>Department</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="department"
                                            value={formData.department}
                                            onChange={handleChange}
                                        />
                                    </Form.Group>
                                )}

                                <Form.Group className="mb-3" controlId="phone">
                                    <Form.Label>Phone Number</Form.Label>
                                    <Form.Control
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                    />
                                </Form.Group>

                                <Form.Group
                                    className="mb-3"
                                    controlId="emailNotifications"
                                >
                                    <Form.Check
                                        type="switch"
                                        role="switch"
                                        label="Send me optional email"
                                        checked={emailNotifications}
                                        onChange={(e) =>
                                            setEmailNotifications(
                                                e.target.checked
                                            )
                                        }
                                        aria-describedby="emailNotificationsHelp"
                                    />
                                    <Form.Text id="emailNotificationsHelp">
                                        Optional email covers updates such as a
                                        new, resubmitted, or withdrawn
                                        application. Email for a required step,
                                        such as a revision request or a
                                        decision, always sends.
                                    </Form.Text>
                                </Form.Group>

                                <div className="d-flex gap-2">
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        disabled={loading || previewLocked}
                                        aria-describedby={
                                            previewLocked
                                                ? PREVIEW_LOCK_NOTE_ID
                                                : undefined
                                        }
                                    >
                                        {loading ? 'Saving...' : 'Save Changes'}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline-primary"
                                        onClick={() => navigate('/profile')}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                                <PreviewLockNote locked={previewLocked} />
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export default EditProfile;
